import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

export function SkillModal({ skill, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    version: "1.0.0",
    type: "conversation",
    status: "testing",
    parameters: [],
    returns: {
      type: "OBJECT",
      description: "Response from the skill execution"
    }
  });

  useEffect(() => {
    if (skill) {
      setFormData({
        name: skill.name || "",
        description: skill.description || "",
        version: skill.version || "1.0.0",
        type: skill.type?.toLowerCase() || "conversation", 
        status: skill.status?.toLowerCase() || "testing",
        parameters: skill.parameters || [],
        returns: skill.returns || {
          type: "OBJECT",
          description: "Response from the skill execution"
        }
      });
    }
  }, [skill]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleParameterChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      parameters: prev.parameters.map((param, i) =>
        i === index ? { ...param, [field]: value } : param
      ),
    }));
  };

  const handleReturnsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      returns: { ...prev.returns, [field]: value },
    }));
  };

  const handleAddParameter = () => {
    setFormData((prev) => ({
      ...prev,
      parameters: [
        ...prev.parameters,
        { name: '', type: 'STRING', required: false, description: '' },
      ],
    }));
  };

  const handleRemoveParameter = (index) => {
    setFormData((prev) => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-medium">
                    {skill ? 'Edit Skill' : 'Create Skill'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Parameters
                      </label>
                      <button
                        type="button"
                        onClick={handleAddParameter}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Parameter
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.parameters.map((param, index) => (
                        <div
                          key={index}
                          className="flex gap-2 items-start p-2 border rounded-md"
                        >
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={param.name}
                              onChange={(e) => handleParameterChange(index, 'name', e.target.value)}
                              placeholder="Name"
                              className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                            <select
                              value={param.type}
                              onChange={(e) => handleParameterChange(index, 'type', e.target.value)}
                              className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="STRING">STRING</option>
                              <option value="NUMBER">NUMBER</option>
                              <option value="BOOLEAN">BOOLEAN</option>
                              <option value="ARRAY">ARRAY</option>
                              <option value="OBJECT">OBJECT</option>
                            </select>
                            <div className="col-span-2">
                              <input
                                type="text"
                                value={param.description}
                                onChange={(e) => handleParameterChange(index, 'description', e.target.value)}
                                placeholder="Description"
                                className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={param.required}
                                onChange={(e) => handleParameterChange(index, 'required', e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-600">
                                Required
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => handleRemoveParameter(index)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <MinusIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Returns
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <select
                        value={formData.returns.type}
                        onChange={(e) => handleReturnsChange('type', e.target.value)}
                        className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                      >
                        <option value="STRING">STRING</option>
                        <option value="NUMBER">NUMBER</option>
                        <option value="BOOLEAN">BOOLEAN</option>
                        <option value="ARRAY">ARRAY</option>
                        <option value="OBJECT">OBJECT</option>
                      </select>
                      <input
                        type="text"
                        value={formData.returns.description}
                        onChange={(e) => handleReturnsChange('description', e.target.value)}
                        placeholder="Return Description"
                        className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                    >
                      {skill ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 